import { createContext, useContext, useEffect, useRef } from "react"
import { useGeneratorTour } from "../hooks/useGeneratorTour"
import "../styles/driver-tour.css"

interface TourContextValue {
  startTour: () => void
  stopTour: () => void
  resetTour: () => void
  isActive: boolean
  currentStep: number
  isTourCompleted: () => boolean
}

const TourContext = createContext<TourContextValue | null>(null)

export interface TourProviderProps {
  children: React.ReactNode
  autoStart?: boolean
}

export const TourProvider: React.FC<TourProviderProps> = ({
  children,
  autoStart = true,
}) => {
  const tour = useGeneratorTour()
  const hasAutoStarted = useRef(false)

  // Auto-start the tour on first visit
  useEffect(() => {
    // Don't auto-start if already active or if we've already auto-started
    if (
      autoStart &&
      !tour.isTourCompleted() &&
      !hasAutoStarted.current &&
      !tour.isActive
    ) {
      // Delay to ensure elements are rendered
      const timeoutId = setTimeout(() => {
        // Double-check conditions before starting
        if (!tour.isActive && !hasAutoStarted.current) {
          hasAutoStarted.current = true
          tour.startTour()
        }
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [autoStart, tour.isTourCompleted, tour.isActive, tour.startTour])

  return <TourContext.Provider value={tour}>{children}</TourContext.Provider>
}

export function useTour() {
  const context = useContext(TourContext)
  if (!context) {
    throw new Error("useTour must be used within a TourProvider")
  }
  return context
}

export function useTourOptional() {
  return useContext(TourContext)
}

export default TourContext
